import type {
  Beleg,
  Knoten,
  PlanKnoten,
  PlanSchritt,
  RohEreignis,
  SchrittStatus,
  Teilauftrag,
  UnteragentKnoten,
  WerkzeugKnoten,
} from "./typen";

/**
 * The fold from event log to transcript.
 *
 * Three things happen here that make a long run readable.
 *
 * A tool call is written twice — once when it starts, once when it returns — and is
 * read once. `werkzeug_ergebnis` is merged into the `werkzeug_start` that shares its
 * `werkzeugAufrufId`, so one lookup is one line rather than two.
 *
 * A plan is written every time it changes and is read as one list. The node keeps its
 * original position, so steps tick off where the user is already looking instead of
 * the plan reappearing further down.
 *
 * A delegated batch is written flat, with each event naming its sub-agent, and is read
 * as a tree. Six properties fanned out over six children collapse to one openable line.
 *
 * The function is pure and recomputed from the whole array. A run is bounded at 500
 * events by the API, so this costs nothing and removes an entire class of bug that
 * incremental merging into React state invites.
 */

/** Tools whose job is to publish the plan rather than to look something up. */
const PLAN_WERKZEUGE = /^(plan_setzen|plan_aktualisieren|aufgabenliste|schrittfolge)$/;

/**
 * Tools that spawn a child run.
 *
 * The kernel implements delegation as a separate `agent_run` whose `anker` names its
 * parent, so the child's own steps are not in this run's log. The node therefore shows
 * the brief and the child's summary; opening it to the child's trace needs an endpoint
 * that does not exist yet, and the fold is written so that arriving events tagged with
 * a sub-agent simply drop into place when it does.
 */
const DELEGATIONS_WERKZEUGE = /^(teilauftrag|unteragent|delegieren)/;

/**
 * @param ereignisse the run's durable log, in order
 * @param ankerPlan the plan from the run row, which is newer than any event once the
 *        run has stopped — the kernel rewrites it whole rather than appending
 */
export function faltEreignisse(
  ereignisse: RohEreignis[],
  ankerPlan: PlanSchritt[] | null = null,
): Knoten[] {
  const wurzel: Knoten[] = [];
  const werkzeuge = new Map<string, WerkzeugKnoten>();
  const unteragenten = new Map<string, UnteragentKnoten>();
  /** Calls whose result is already represented better by another node. */
  const stumm = new Set<string>();
  let plan: PlanKnoten | null = null;

  /** Where an event belongs: inside its sub-agent, or at the top of the turn. */
  const ziel = (e: RohEreignis): Knoten[] => {
    const marke = unteragentMarke(e.detail);
    if (!marke) return wurzel;

    let knoten = unteragenten.get(marke.id);
    if (!knoten) {
      knoten = {
        art: "unteragent",
        schluessel: `ua-${marke.id}`,
        id: marke.id,
        titel: marke.titel,
        status: "laeuft",
        zusammenfassung: null,
        teilauftraege: [],
        kinder: [],
      };
      unteragenten.set(marke.id, knoten);
      wurzel.push(knoten);
    }
    // A later event may carry a better title than the one that opened the node.
    if (marke.titel && knoten.titel === marke.id) knoten.titel = marke.titel;
    return knoten.kinder;
  };

  for (const e of ereignisse) {
    const detail = e.detail ?? {};

    switch (e.art) {
      case "text": {
        const text = typeof detail.text === "string" ? detail.text : e.titel;
        if (text.trim()) {
          ziel(e).push({ art: "text", schluessel: `t-${e.seq}`, text });
        }
        break;
      }

      case "werkzeug_start": {
        // A plan tool carries its steps in the call's own input, which is written to
        // the log the moment the call starts. That is what lets the list tick live
        // rather than only appearing once the run has stopped.
        if (PLAN_WERKZEUGE.test(e.titel)) {
          // The plan tool's own result is the plan restated as a line of text. The
          // list renders it properly, so the call itself never becomes a step.
          if (e.werkzeugAufrufId) stumm.add(e.werkzeugAufrufId);
          const schritte = planAus(detail) ?? planAus(alsObjekt(detail.eingabe));
          if (schritte) plan = setzePlan(plan, wurzel, e, schritte);
          break;
        }

        if (DELEGATIONS_WERKZEUGE.test(e.titel) && !unteragentMarke(detail)) {
          const id = e.werkzeugAufrufId ?? `s${e.seq}`;
          const auftraege = teilauftraegeAus(alsObjekt(detail.eingabe));
          const knoten: UnteragentKnoten = {
            art: "unteragent",
            schluessel: `ua-${id}`,
            id,
            titel:
              auftraege.length === 1 ? (auftraege[0]?.titel ?? "Teilauftrag") : "Teilaufträge",
            status: "laeuft",
            zusammenfassung: null,
            teilauftraege: auftraege,
            kinder: [],
          };
          unteragenten.set(id, knoten);
          wurzel.push(knoten);
          break;
        }

        const knoten: WerkzeugKnoten = {
          art: "werkzeug",
          schluessel: `w-${e.werkzeugAufrufId ?? e.seq}`,
          name: e.titel,
          eingabe: detail.eingabe ?? null,
          ergebnis: null,
          belege: [],
          status: "laeuft",
          risikoklasse: e.risikoklasse ?? null,
        };
        if (e.werkzeugAufrufId) werkzeuge.set(e.werkzeugAufrufId, knoten);
        ziel(e).push(knoten);
        break;
      }

      case "werkzeug_ergebnis": {
        if (e.werkzeugAufrufId && stumm.has(e.werkzeugAufrufId)) break;
        const belege = belegeAus(detail.verweise);

        const kind = e.werkzeugAufrufId ? unteragenten.get(e.werkzeugAufrufId) : undefined;
        if (kind) {
          kind.status = "fertig";
          kind.zusammenfassung = e.titel;
          break;
        }

        const offen = e.werkzeugAufrufId ? werkzeuge.get(e.werkzeugAufrufId) : undefined;
        if (offen) {
          offen.status = detail.fehler ? "fehler" : "fertig";
          offen.ergebnis = e.titel;
          offen.belege = belege;
          break;
        }

        // No matching start: still show it rather than swallow a result.
        ziel(e).push({
          art: "werkzeug",
          schluessel: `w-${e.seq}`,
          name: textAus(detail.werkzeug) ?? "Schritt",
          eingabe: null,
          ergebnis: e.titel,
          belege,
          status: "fertig",
          risikoklasse: e.risikoklasse ?? null,
        });
        break;
      }

      case "checkpoint": {
        ziel(e).push({
          art: "freigabe",
          schluessel: `f-${e.seq}`,
          freigabeId: textAus(detail.freigabeId),
          titel: e.titel,
          risikoklasse: e.risikoklasse ?? null,
        });
        break;
      }

      case "fehler": {
        ziel(e).push({
          art: "fehler",
          schluessel: `e-${e.seq}`,
          text: textAus(detail.text) ?? e.titel,
        });
        const marke = unteragentMarke(detail);
        if (marke) {
          const knoten = unteragenten.get(marke.id);
          if (knoten) knoten.status = "fehler";
        }
        break;
      }

      case "status": {
        const schritte = planAus(detail);
        if (schritte) {
          plan = setzePlan(plan, wurzel, e, schritte);
          break;
        }

        // The kernel announces each spawned child with its own status event, keyed to
        // the `teilauftrag_starten` call that spawned it. That is how the container
        // learns the run ids of its children.
        const kindRunId = textAus(detail.kindRunId);
        if (kindRunId) {
          const behaelter = e.werkzeugAufrufId ? unteragenten.get(e.werkzeugAufrufId) : undefined;
          const titel = e.titel.replace(/^Teilauftrag gestartet:\s*/i, "").trim() || e.titel;
          if (behaelter) {
            const vorhanden = behaelter.teilauftraege.find((t) => t.titel === titel);
            if (vorhanden) vorhanden.kindRunId = kindRunId;
            else behaelter.teilauftraege.push({ titel, kindRunId, auftrag: null });
          } else {
            ziel(e).push({ art: "notiz", schluessel: `n-${e.seq}`, text: e.titel, leise: false });
          }
          break;
        }

        const marke = unteragentMarke(detail);
        if (marke && typeof detail.status === "string") {
          const knoten = unteragenten.get(marke.id);
          if (knoten && (detail.status === "fertig" || detail.status === "fehler")) {
            knoten.status = detail.status;
          }
          break;
        }
        ziel(e).push({ art: "notiz", schluessel: `n-${e.seq}`, text: e.titel, leise: false });
        break;
      }

      case "denken": {
        ziel(e).push({
          art: "notiz",
          schluessel: `n-${e.seq}`,
          text: textAus(detail.text) ?? e.titel,
          leise: true,
        });
        break;
      }

      // `kosten` is read off the run row, which carries the authoritative total, and
      // `abschluss` is the run's status — neither belongs in the transcript body.
      case "kosten":
      case "abschluss":
        break;
    }
  }

  // The run row holds the plan's latest state; the log holds the order things
  // happened in. Take the position from the log and the contents from the row.
  if (ankerPlan && ankerPlan.length > 0) {
    if (plan) plan.schritte = ankerPlan;
    else wurzel.unshift({ art: "plan", schluessel: "plan", titel: null, schritte: ankerPlan });
  }

  return wurzel;
}

/** Reads `agent_run.anker.plan` into the panel's step shape. */
export function planAusAnker(anker: Record<string, unknown> | null | undefined): PlanSchritt[] | null {
  return anker ? planAus(anker) : null;
}

function setzePlan(
  vorhanden: PlanKnoten | null,
  wurzel: Knoten[],
  e: RohEreignis,
  schritte: PlanSchritt[],
): PlanKnoten {
  const titel = textAus((e.detail ?? {}).planTitel);
  if (vorhanden) {
    vorhanden.schritte = schritte;
    if (titel) vorhanden.titel = titel;
    return vorhanden;
  }
  const neu: PlanKnoten = { art: "plan", schluessel: "plan", titel, schritte };
  wurzel.push(neu);
  return neu;
}

/**
 * Reads a plan out of an event detail.
 *
 * Deliberately tolerant about the wire shape: the loop is being built in parallel and
 * the panel should render the plan the first time one is emitted, not the first time
 * both sides agree on a field name. `schritte`, `plan` and `aufgaben` are accepted,
 * each either a string or an object with a text and a status.
 */
function planAus(detail: Record<string, unknown>): PlanSchritt[] | null {
  const roh = detail.schritte ?? detail.plan ?? detail.aufgaben;
  if (!Array.isArray(roh) || roh.length === 0) return null;

  const schritte: PlanSchritt[] = [];
  for (const eintrag of roh) {
    if (typeof eintrag === "string") {
      schritte.push({ text: eintrag, status: "offen" });
      continue;
    }
    if (!eintrag || typeof eintrag !== "object") continue;
    const objekt = eintrag as Record<string, unknown>;
    const text = textAus(objekt.text) ?? textAus(objekt.titel) ?? textAus(objekt.schritt);
    if (!text) continue;
    schritte.push({ text, status: schrittStatus(objekt.status ?? objekt.zustand) });
  }

  return schritte.length > 0 ? schritte : null;
}

function schrittStatus(roh: unknown): SchrittStatus {
  const wert = typeof roh === "string" ? roh.toLowerCase() : "";
  if (["erledigt", "fertig", "abgeschlossen", "done", "completed"].includes(wert)) return "erledigt";
  if (["uebersprungen", "übersprungen", "entfaellt", "entfällt", "skipped"].includes(wert)) {
    return "uebersprungen";
  }
  if (["laeuft", "läuft", "aktiv", "in_arbeit", "in_progress"].includes(wert)) return "laeuft";
  if (["gescheitert", "fehler", "failed"].includes(wert)) return "gescheitert";
  return "offen";
}

/** Reads the brief list out of a `teilauftrag_starten` call's input. */
function teilauftraegeAus(eingabe: Record<string, unknown>): Teilauftrag[] {
  const roh = eingabe.auftraege;
  if (!Array.isArray(roh)) return [];

  const auftraege: Teilauftrag[] = [];
  for (const eintrag of roh) {
    const objekt = alsObjekt(eintrag);
    const titel = textAus(objekt.titel);
    const auftrag = textAus(objekt.auftrag);
    if (!titel && !auftrag) continue;
    auftraege.push({ titel: titel ?? kuerze(auftrag) ?? "Teilauftrag", kindRunId: null, auftrag });
  }
  return auftraege;
}

function alsObjekt(wert: unknown): Record<string, unknown> {
  return wert && typeof wert === "object" && !Array.isArray(wert)
    ? (wert as Record<string, unknown>)
    : {};
}

function kuerze(text: string | null): string | null {
  if (!text) return null;
  const flach = text.replace(/\s+/g, " ").trim();
  return flach.length > 60 ? `${flach.slice(0, 60)}…` : flach;
}

interface UnteragentMarke {
  id: string;
  titel: string;
}

/** Accepts either a nested `unteragent` object or flat `unteragentId`/`unteragentTitel`. */
function unteragentMarke(detail: Record<string, unknown> | null | undefined): UnteragentMarke | null {
  if (!detail) return null;

  const verschachtelt = detail.unteragent;
  if (verschachtelt && typeof verschachtelt === "object") {
    const objekt = verschachtelt as Record<string, unknown>;
    const id = textAus(objekt.id);
    if (id) return { id, titel: textAus(objekt.titel) ?? id };
  }

  const id = textAus(detail.unteragentId);
  if (id) return { id, titel: textAus(detail.unteragentTitel) ?? id };

  return null;
}

function belegeAus(roh: unknown): Beleg[] {
  if (!Array.isArray(roh)) return [];
  const belege: Beleg[] = [];
  for (const eintrag of roh) {
    if (!eintrag || typeof eintrag !== "object") continue;
    const objekt = eintrag as Record<string, unknown>;
    const label = textAus(objekt.label);
    const href = textAus(objekt.href);
    // Only in-app routes. A model-invented absolute URL is not a Beleg.
    if (label && href && href.startsWith("/")) belege.push({ label, href });
  }
  return belege;
}

function textAus(wert: unknown): string | null {
  return typeof wert === "string" && wert.trim() ? wert : null;
}
